import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { switchMap } from 'rxjs';

import { IBetslipRootState } from '../../../base/store/state';
import { PickId } from '../../../core/picks/pick-id';
import { EditBetActions } from '../../edit-bet/actions';
import BetslipPickActions from '../../picks/actions';
import { removePicksFromGroup } from '../../picks/grouped-picks.actions';
import BetslipErrorsActions from '../actions';
import { GroupWithClosedLegsError } from '../errors/general/group-pick-error';
import { selectEditBetPicksWithErrors, selectPicksWithCurrentErrors } from '../selectors';
import {
    getClosedLegIdsWithinValidGroups,
    isPickStatusClosed,
    isPickStatusLocked,
    isPickStatusOddsChanged,
} from '../services/utils/betslip-errors-utils';

@Injectable()
export default class DigitalAcceptChangesEffects {
    constructor(
        private actions$: Actions,
        private store: Store<IBetslipRootState>,
    ) {}

    handleAcceptEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipErrorsActions.acceptBetslipChanges),
            concatLatestFrom(() => this.store.select(selectPicksWithCurrentErrors)),
            switchMap(([_, state]) => {
                const actions = [];
                const toRemove: PickId[] = [];
                const toDeselect: PickId[] = [];
                const toUpdate: PickId[] = [];
                for (const pickWithErrors of state) {
                    if (pickWithErrors.errors.some(isPickStatusClosed)) {
                        toRemove.push(pickWithErrors.pick.id);
                    } else if (pickWithErrors.errors.some(isPickStatusLocked)) {
                        toDeselect.push(pickWithErrors.pick.id);
                    } else if (pickWithErrors.errors.some(isPickStatusOddsChanged)) {
                        toUpdate.push(pickWithErrors.pick.id);
                    } else if (pickWithErrors.errors.some((t) => t instanceof GroupWithClosedLegsError)) {
                        const legsToRemove = getClosedLegIdsWithinValidGroups([pickWithErrors.pick], {
                            [pickWithErrors.pick.id.toString()]: pickWithErrors.errors,
                        });
                        if (legsToRemove.length) {
                            actions.push(
                                removePicksFromGroup({
                                    groupInfo: pickWithErrors.pick.groupInfo!,
                                    pickIds: legsToRemove,
                                }),
                            );
                        }
                    }
                }
                if (toUpdate.length) {
                    actions.push(BetslipPickActions.acceptCurrentPrice({ pickIds: toUpdate, isManuallyAccepted: true }));
                }
                if (toDeselect.length) {
                    actions.push(BetslipPickActions.changeSelection({ pickIds: toDeselect, selection: false }));
                }
                if (toRemove.length) {
                    actions.push(BetslipPickActions.requestRemoveMultiplePicks({ pickIds: toRemove }));
                }

                return actions;
            }),
        );
    });

    handleAcceptEditBetEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipErrorsActions.acceptBetslipEditBetChanges),
            concatLatestFrom(() => this.store.select(selectEditBetPicksWithErrors)),
            switchMap(([_, state]) => {
                const toRemove: PickId[] = [];
                const toDeselect: PickId[] = [];
                const toUpdate: PickId[] = [];
                for (const pickWithErrors of state) {
                    if (pickWithErrors.errors.some(isPickStatusClosed)) {
                        toRemove.push(pickWithErrors.pick.id);
                    } else if (pickWithErrors.errors.some(isPickStatusLocked)) {
                        toDeselect.push(pickWithErrors.pick.id);
                    } else if (pickWithErrors.errors.some(isPickStatusOddsChanged)) {
                        toUpdate.push(pickWithErrors.pick.id);
                    }
                }

                return [
                    BetslipPickActions.acceptCurrentPrice({ pickIds: toUpdate, isManuallyAccepted: true }),
                    BetslipPickActions.changeSelection({ pickIds: toDeselect, selection: false }),
                    ...toRemove.map((pickId) => EditBetActions.removePick({ pickId: pickId.toString() })),
                ];
            }),
        );
    });
}
